import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { RiskBadge } from "@/components/StatusBadge";
import { BOARD_FILTERS, needsAttention } from "@/lib/format";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const VIEWS = [
  { key: "all", label: "All Hot Shipments" },
  { key: "next-action", label: "Overdue Next Actions", issue: "Next action overdue" },
  { key: "missing-docs", label: "Missing Documents", issue: "Missing documents" },
  { key: "customer-update", label: "Customer Update Due", issue: "Customer update overdue" },
  { key: "delayed", label: "Delayed", issue: "Shipment delayed" },
  { key: "no-update", label: "No Update in 24h", issue: "No update in 24h+" },
  { key: "pod-missing", label: "POD Missing", issue: "Proof of delivery missing" },
  { key: "ready-to-close", label: "Ready to Close", ready: true },
  { key: "closeout-blocked", label: "Closeout Blocked", blocked: true },
];

export default function NeedsAttention() {
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({ owner: "all", customer: "all", risk: "all" });
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const view = params.get("view") || "all";
  const setView = (v) => {
    if (v === "all") params.delete("view");
    else params.set("view", v);
    setParams(params, { replace: true });
  };

  useEffect(() => {
    client.get("/shipments").then((r) => setJobs(r.data));
  }, []);

  const flagged = useMemo(
    () => jobs.filter((j) => needsAttention(j)),
    [jobs]
  );

  const owners = useMemo(() => [...new Set(flagged.map((j) => j.assigned_owner))].sort(), [flagged]);
  const customers = useMemo(() => [...new Set(flagged.map((j) => j.customer_name))].sort(), [flagged]);
  const risks = useMemo(() => [...new Set(flagged.map((j) => j.risk_status))], [flagged]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const viewDef = VIEWS.find((v) => v.key === view) || VIEWS[0];

  const base = useMemo(() => {
    if (viewDef.ready) return jobs.filter(BOARD_FILTERS["ready-to-close"]);
    if (viewDef.blocked) return jobs.filter(BOARD_FILTERS["closeout-blocked"]);
    if (viewDef.issue) return flagged.filter((j) => j.issues.some((i) => i.startsWith(viewDef.issue)));
    return flagged;
  }, [jobs, flagged, viewDef]);

  const filtered = base.filter((j) => {
    if (filters.owner !== "all" && j.assigned_owner !== filters.owner) return false;
    if (filters.customer !== "all" && j.customer_name !== filters.customer) return false;
    if (filters.risk !== "all" && j.risk_status !== filters.risk) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => b.issues.length - a.issues.length);

  const count = (v) => {
    if (v.ready) return jobs.filter(BOARD_FILTERS["ready-to-close"]).length;
    if (v.issue) return flagged.filter((j) => j.issues.some((i) => i.startsWith(v.issue))).length;
    return flagged.length;
  };

  const FilterSelect = ({ label, k, value, options, testid }) => (
    <div className="min-w-[150px]">
      <label className="op-label block mb-1">{label}</label>
      <Select value={value} onValueChange={(vv) => set(k, vv)}>
        <SelectTrigger data-testid={testid} className="h-9 border-line rounded-sm bg-surface text-[13px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const anyFilter = filters.owner !== "all" || filters.customer !== "all" || filters.risk !== "all";

  return (
    <Layout>
      <div className="mb-5">
        <p className="op-label mb-1 text-warn">Operational priority</p>
        <h1 className="font-heading text-2xl font-semibold text-ink flex items-center gap-2">
          Hot Shipments
        </h1>
        <p className="text-sm text-ink-2 mt-1">Every job that's stuck — why it's stuck, who owns it, and what needs to happen next.</p>
      </div>

      {/* Quick views */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            data-testid={`na-view-${v.key}`}
            onClick={() => setView(v.key)}
            className={`h-8 px-3 rounded-sm text-[12.5px] font-medium border transition-colors flex items-center gap-1.5 ${
              view === v.key ? "bg-brand text-white border-brand" : "bg-surface text-ink-2 border-line hover:border-steel"
            }`}
          >
            {v.label}
            <span className={`font-mono-data text-[10.5px] ${view === v.key ? "text-white/70" : "text-ink-3"}`}>{count(v)}</span>
          </button>
        ))}
      </div>

      {/* Secondary filters */}
      <div className="bg-surface border border-line rounded-md p-4 mb-4 flex flex-wrap gap-3 items-end">
        <FilterSelect label="Owner" k="owner" value={filters.owner} options={owners} testid="filter-owner" />
        <FilterSelect label="Customer" k="customer" value={filters.customer} options={customers} testid="filter-customer" />
        <FilterSelect label="Risk status" k="risk" value={filters.risk} options={risks} testid="filter-risk" />
        {anyFilter && (
          <button data-testid="na-clear-filters" onClick={() => setFilters({ owner: "all", customer: "all", risk: "all" })} className="h-9 px-2.5 rounded-sm text-[13px] text-ink-2 hover:text-danger flex items-center gap-1 transition-colors">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
        <div className="ml-auto text-[13px] text-ink-2 font-mono-data">{sorted.length} shown</div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-line rounded-md overflow-hidden">
        <div className="overflow-x-auto mv-scroll">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-alt">
                {["Job #", "Customer", "Issue", "Owner", "Next Action", "Age", "Risk Status", ""].map((h) => (
                  <th key={h} className="op-label px-4 py-2.5 whitespace-nowrap font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {sorted.map((j) => (
                <tr
                  key={j.id}
                  data-testid={`attention-row-${j.job_number}`}
                  tabIndex={0}
                  onClick={() => navigate(`/app/shipments/${j.id}`)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/app/shipments/${j.id}`); } }}
                  className="hover:bg-surface-alt transition-colors cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel/40"
                >
                  <td className="px-4 py-3 font-mono-data text-[12.5px] text-ink font-medium whitespace-nowrap">{j.job_number}</td>
                  <td className="px-4 py-3 text-[13.5px] text-ink whitespace-nowrap">{j.customer_name}</td>
                  <td className="px-4 py-3">
                    {j.issues.length > 0 ? (() => {
                      const primary = j.issues[0];
                      const tone = "border-warn/50 bg-warn/10 text-warn";
                      return (
                        <div className="flex items-center gap-2">
                          <span data-testid={`attention-primary-${j.job_number}`} className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[11px] font-medium whitespace-nowrap ${tone}`}>{primary}</span>
                          {j.issues.length > 1 && (
                            <span data-testid={`attention-more-${j.job_number}`} title={j.issues.slice(1).join(" · ")} className="text-[11px] font-mono-data text-ink-3 whitespace-nowrap">+{j.issues.length - 1} more</span>
                          )}
                        </div>
                      );
                    })() : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-ok/30 bg-ok/10 text-ok text-[11px] font-medium">Ready to close</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.assigned_owner}</td>
                  <td className="px-4 py-3 text-[13px] text-ink-2 max-w-[220px] truncate">{j.next_action}</td>
                  <td className="px-4 py-3 font-mono-data text-[12.5px] whitespace-nowrap" style={{ color: j.age_days > 1 ? "#3F566E" : "#7B8794" }}>{j.age_days}d</td>
                  <td className="px-4 py-3 whitespace-nowrap"><RiskBadge status={j.risk_status} /></td>
                  <td className="px-4 py-3"><ArrowRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" /></td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-ink-3">No jobs match this view. Nothing stuck — good place to be.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
